'use client';

import { useTranslations } from 'next-intl';
import { Loader2, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useHashDialog } from '@/hooks/use-hash-dialog';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { useBulkDeleteAssignmentsMutation } from '../services/handover-scanning.mutations';
import { HANDOVER_SCANNING_HASH } from '../constants';
import type { WorkloadListParams } from '../types';

interface WorkloadTableActionBarProps {
  selectedCount: number;
  getBulkPayload: () =>
    | { ids: number[] }
    | { all: true; filters: Partial<WorkloadListParams> };
  onSuccess?: () => void;
}

export function WorkloadTableActionBar({
  selectedCount,
  getBulkPayload,
  onSuccess
}: WorkloadTableActionBarProps) {
  const t = useTranslations('handoverScanning');
  // Bulk-delete confirmation open-state lives in the URL hash (#bulk-delete)
  // so it is deep-linkable and closes on browser Back.
  const { open: deleteOpen, onOpenChange: toggleDelete } = useHashDialog(
    HANDOVER_SCANNING_HASH.bulkDelete
  );

  const bulkDeleteMutation = useBulkDeleteAssignmentsMutation();

  async function handleBulkDelete() {
    const payload = getBulkPayload();
    try {
      await bulkDeleteMutation.mutateAsync(payload);
      onSuccess?.();
    } catch {
      // handled by global MutationCache
    }
    toggleDelete(false);
  }

  return (
    <>
      <div className='bg-muted flex items-center gap-3 rounded-lg px-4 py-2'>
        <span className='text-muted-foreground text-sm font-medium'>
          {t('selectedCount', { count: selectedCount })}
        </span>
        <div className='flex items-center gap-2'>
          <Button
            variant='destructive'
            size='sm'
            onClick={() => toggleDelete(true)}
          >
            <Trash2 className='me-1.5 h-3.5 w-3.5' />
            {t('bulkDelete')}
          </Button>
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={toggleDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bulkDeleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('bulkDeleteConfirm', { count: selectedCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {bulkDeleteMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
